import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import '@chancenlogistik/client/components/logistik-component-base-header.js';
import '@chancenlogistik/client/components/logistik-component-base-page-layout.js';
import { colors } from '@chancenwerk/client/design/theme.js';
import { LogistikProductAPI } from '@chancenwerk/db/api/LogistikProductAPI.js';
import { LogistikProductOrderingAPI } from '@chancenwerk/db/api/LogistikProductOrderingAPI.js';
import type { LogistikProductCategory } from '@chancenwerk/db/enums/LogistikEnums.js';
import type { LogistikProduct } from '@chancenwerk/db/LogistikProduct.js';
import { LogistikProductOrdering } from '@chancenwerk/db/LogistikProductOrdering.js';
import { LogistikRoutes } from '@chancenwerk/logistik/client/routes.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { map, tap } from 'rxjs';
import { mainScrollContainer } from '../globals.js';

const productSelectionSet = {
  title: 1,
  imageID: 1,
  _id: 1,
} as const;

enum SavingState {
  SAVED = 'saved',
  SAVING = 'saving',
  FAILED = 'failed',
}

export const LogistikComponentProductOrderingEditor = webcomponent(
  'logistik-component-product-ordering-editor',
  (props: { category: LogistikProductCategory }) => {
    const [category] = useState(props.category);
    const [savingState, setSavingState] = useState(SavingState.SAVED);
    const ordering = useObservable(
      () =>
        LogistikProductOrderingAPI.subscribeByCategory(category as string)(
          selectionSet(() => LogistikProductOrdering, 1),
        ).pipe(
          tap(async ord => {
            if (ord) return;
            await new LogistikProductOrdering({ productIDs: [], productCategory: category }).create();
          }),
        ),
      [category],
    );
    const products = useObservable(
      () =>
        LogistikProductAPI.subscribeByProductCategory(category)(productSelectionSet).pipe(
          map(ps => ps as Array<NarrowedEntity<LogistikProduct, typeof productSelectionSet>>),
        ),
      [category],
    );

    if (ordering === undefined || products === undefined) return html`<cr-loading-state></cr-loading-state>`;

    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header
          pageTitle=${`Produktreihenfolge (${category})`}
          backRoute=${LogistikRoutes.orderings}
        >
          ${SavingStateIndicator(savingState)}
        </logistik-component-base-header>
        ${Content(ordering, products, async (ids: string[]) => {
          if (ordering && !arrayEq(ordering.productIDs, ids)) {
            ordering.productIDs = ids;
            setSavingState(SavingState.SAVING);
            if (ordering._id) await ordering.save().catch(err => console.error(err));
            else await ordering.create().catch(err => console.error(err));
            setSavingState(SavingState.SAVED);
          }
        })}
      </logistik-component-base-page-layout>
    `;
  },
);

function SavingStateIndicator(savingState: SavingState): Renderable {
  return {
    [SavingState.SAVED]: SavingStateChip('check', 'gespeichert', colors.chancen_green_primary),
    [SavingState.SAVING]: SavingStateChip('sync', 'wird gespeichert', colors.chancen_blue_accent),
    [SavingState.FAILED]: SavingStateChip('error', 'Speichern fehlgeschlagen', colors.red),
  }[savingState];
}

function SavingStateChip(icon: string, text: string, color: string) {
  return html`
    <cr-chip ${css({ backgroundColor: color, color: 'white' })}>
      <cr-flex center crossaxis-center padding="xs">
        <cr-icon> ${icon} </cr-icon>
        <cr-tooltip>
          <cr-text> ${text} </cr-text>
        </cr-tooltip>
      </cr-flex>
    </cr-chip>
  `;
}

function Content(
  ordering: LogistikProductOrdering,
  products: Array<NarrowedEntity<LogistikProduct, typeof productSelectionSet>>,
  onValuePicked?: (value: string[]) => void,
): Renderable {
  if (!products.length)
    return html`
      <cr-flex w100 h100 center crossaxis-center padding="xxl">
        <cr-h4> Es wurden keine Produkte für diese Kategorie gefunden. </cr-h4>
      </cr-flex>
    `;
  return html`
    <product-ordering-drag-drop
      .value=${ordering.productIDs}
      .products=${products}
      @value-picked=${(e: ValueEvent<string[]>) => onValuePicked?.(e.detail.value)}
    >
    </product-ordering-drag-drop>
  `;
}

@customElement('product-ordering-drag-drop')
export class ProductOrderingDragDrop extends FormField<string[]> {
  @property({ attribute: false }) products!: Array<NarrowedEntity<LogistikProduct, typeof productSelectionSet>>;

  override connectedCallback(): void {
    super.connectedCallback();

    const allProductIDs = this.products.map(p => p._id);
    if (!this.value.value?.length) {
      this._setNewValue(allProductIDs);
    } else {
      this._setNewValue(mergeProductIDs(this.value.value, allProductIDs));
    }
  }

  override render() {
    if (this.value.isLoading || !this.value.value) return html`<cr-loading-state></cr-loading-state>`;
    return html`
      <cr-drag-drop
        drag-mode="column"
        allow-from="cr-flex.draggable"
        child-selector="cr-flex.draggable"
        @resorted=${(e: CustomEvent<{ order: string[]; start: number; end: number }>) =>
          this._setNewValue(e.detail.order)}
        .autoScrollContainer=${() => mainScrollContainer.value}
      >
        <cr-flex w100 h100>
          ${repeat(
            this.value.value,
            a => a,
            (pid, idx) => {
              const product = this.products.find(p => p._id === pid);
              if (!product) return nothing;
              else
                return ProductItem(
                  product,
                  idx + 1,
                  (nidx: number) => {
                    if (!this.value.value) return;
                    const element = this.value.value[idx];
                    if (!element) return;
                    const dst = Math.max(0, Math.min(this.value.value.length - 1, nidx - 1));
                    const value = [...this.value.value];
                    value.splice(idx, 1);
                    value.splice(dst, 0, element);
                    this._setNewValue(value);
                  },
                  idx === 0,
                  idx === (this.value.value?.length ?? 0) - 1,
                );
            },
          )}
        </cr-flex>
      </cr-drag-drop>
    `;
  }

  private _setNewValue(newValue: string[]) {
    this.value.next(newValue);
    this.dispatchEvent(
      new CustomEvent('value-picked', {
        detail: { value: this.value.value },
      }),
    );
  }
}

function mergeProductIDs(current: string[], all: string[]): string[] {
  return all.filter(s => !current.includes(s)).concat(current.filter(s => all.includes(s)));
}

function arrayEq(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function ProductItem(
  product: NarrowedEntity<LogistikProduct, { title: 1; imageID: 1; _id: 1 }>,
  idx: number,
  onIndexChanged?: (index: number) => void,
  isFirst: boolean = false,
  isLast: boolean = false,
): Renderable {
  const onEvent = (e: ValueEvent<number>) => {
    if (idx !== e.detail.value) onIndexChanged?.(e.detail.value);
  };
  return html`
    <cr-flex ${css({ userSelect: 'none' })} class="draggable" w100 id=${product._id} key=${product._id} padding="sm">
      <cr-card
        ${css({
          width: '100%',
          height: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
        })}
        space="md"
      >
        <cr-flex space="sm" horizontal crossaxis-center>
          <cr-flex space-between>
            <cr-button
              mode="icon"
              icon="keyboard_arrow_up"
              @click=${() => onIndexChanged?.(idx - 1)}
              ?disabled=${isFirst}
            >
            </cr-button>
            <cr-button
              mode="icon"
              icon="keyboard_arrow_down"
              @click=${() => onIndexChanged?.(idx + 1)}
              ?disabled=${isLast}
            >
            </cr-button>
          </cr-flex>
          <cr-input
            .value=${idx}
            type="number"
            @value-picked=${onEvent}
            @submit=${onEvent}
            ${css({ width: '60px' })}
          ></cr-input>
        </cr-flex>
        <cr-flex space="sm" horizontal w100 crossaxis-center>
          ${Image(product.imageID)}
          <cr-h4> ${product.title} </cr-h4>
        </cr-flex>
        <cr-flex center crossaxis-center>
          <cr-icon>drag_indicator</cr-icon>
        </cr-flex>
      </cr-card>
    </cr-flex>
  `;
}

function Image(imageID: string): Renderable {
  const N = 30;
  const size = `${N}x${N}`;
  const width = `${N}px`;
  const height = `${N}px`;
  return html`
    <cr-flex flex-none>
      <img src=${ImageFile.getServeLink(imageID, { size })} ${css({ width, height })} />
    </cr-flex>
  `;
}
